import React from 'react';
import { MenuPrintConfig, MenuPrintPageProps } from './types';
import NewYorkTap from './NewYorkTap';
import NewYorkGroupHeader from './NewYorkGroupHeader';

interface NewYorkColumnComponentProps {
  data: MenuPrintPageProps['columns'][number];
  config: MenuPrintConfig;
}

interface GroupSection {
  groupName: string;
  taps: any[];
}

const splitIntoGroups = (data: any[]): GroupSection[] => {
  const groups: GroupSection[] = [];
  let current: GroupSection | null = null;

  for (const entry of data) {
    if (entry.groupName) {
      if (current) groups.push(current);
      current = { groupName: entry.groupName, taps: [] };
    } else {
      if (!current) {
        current = { groupName: '', taps: [] };
      }
      current.taps.push(entry);
    }
  }
  if (current) groups.push(current);
  return groups;
};

const chunkRows = (items: any[], columnsPerRow: number) => {
  const rows: any[][] = [];
  for (let i = 0; i < items.length; i += columnsPerRow) {
    rows.push(items.slice(i, i + columnsPerRow));
  }
  return rows;
};

const NewYorkColumnComponent: React.FC<NewYorkColumnComponentProps> = ({ data, config }) => {
  const isGrouped = config.groupBy !== 'none';
  const columnCount = config.columnCount || 2;

  if (isGrouped) {
    const groups = splitIntoGroups(data);

    return (
      <div>
        {groups.map((group, gi) => (
          <div key={`ny-group-${gi}`}>
            {group.groupName && <NewYorkGroupHeader groupName={group.groupName} isFirst={gi === 0} />}
            {chunkRows(group.taps, columnCount).map((row, rowIndex, rows) => {
              const isLastRow = rowIndex === rows.length - 1;
              return (
                <div
                  key={`group-row-${gi}-${rowIndex}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
                    borderBottom: isLastRow ? 'none' : '1px solid #000'
                  }}>
                  {Array.from({ length: columnCount }).map((_, colIndex) => {
                    const tap = row[colIndex];
                    const borderLeft = colIndex > 0 ? '1px solid #000' : 'none';
                    const cellPadding = colIndex > 0 ? { paddingLeft: 16, paddingRight: 10 } : { paddingLeft: 10, paddingRight: 16 };

                    return (
                      <div
                        key={tap?._id || `empty-${gi}-${rowIndex}-${colIndex}`}
                        style={{
                          borderLeft,
                          breakInside: 'avoid',
                          pageBreakInside: 'avoid',
                          minHeight: 1,
                          ...cellPadding
                        }}>
                        {tap ? <NewYorkTap tap={tap} config={config} grouped /> : null}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
        columnGap: 0,
        paddingTop: 8
      }}>
      {data.map((entry: any, i: number) => {
        if (!entry || entry.groupName) return null;
        const colIndex = i % columnCount;
        const cellStyle: React.CSSProperties =
          colIndex > 0
            ? { paddingLeft: 16, paddingRight: 10, breakInside: 'avoid', pageBreakInside: 'avoid' }
            : { paddingLeft: 10, paddingRight: 16, breakInside: 'avoid', pageBreakInside: 'avoid' };
        return (
          <div key={entry._id || `tap-${i}`} style={cellStyle}>
            <NewYorkTap tap={entry} config={config} />
          </div>
        );
      })}
    </div>
  );
};

export default NewYorkColumnComponent;
